import { Anchor, Stack, Text, Tooltip } from '@mantine/core';
import { PiMoney } from 'react-icons/pi';
import { Link } from 'wouter';
import { PaymentLedger } from '@/components/InvoiceDetailsModal/ClientReports';
import {
  DocumentCard,
  FactRow,
  money,
} from '@/components/InvoiceDetailsModal/DocumentCard';
import { CollectedIndicator } from '@/components/InvoiceDetailsModal/DocumentHeader';
import { invoiceActionGates } from '@/components/InvoiceDetailsModal/invoiceActions';
import {
  billedRates,
  type InvoiceDetails,
  type TDocumentFx,
  type TFinalInvoiceDetails,
  useInvoiceDetails,
} from '@/models/invoices';
import { usePermissions } from '@/models/permissions';

type FxRate = TDocumentFx['rates'][number];
const rateText = (r: FxRate) =>
  `1 ${r.from} = ${Number(r.rate.toFixed(6))} ${r.to}`;
const dayOf = (r: FxRate, fx: TDocumentFx) =>
  (r.on ?? fx.asOf).split('-').reverse().join('.');

const WHAT: Record<string, (r: FxRate) => string> = {
  line: (r) =>
    `Services the offer prices in ${r.from} are billed here in ${r.to} at this rate.`,
  share: (r) =>
    `This installment's share of the services the offer prices in ${r.from} is billed in ${r.to} at this rate.`,
  base: (r) =>
    `The installment was agreed in ${r.from} and is billed in ${r.to} at this rate.`,
  due: (r) => `The amount is due in ${r.to}, converted at this rate.`,
  plan: (r) =>
    `The plan was agreed in ${r.to}; services the offer prices in ${r.from} were valued at this rate.`,
};
const WHY: Record<string, string> = {
  'offer rate': "The offer's rate",
  'currency changed': 'Taken when the currency was changed',
  issue: 'The rate of the day the proforma was issued',
};

/** What the rate converts, which rate it is and its day - the tooltip of a rate. */
const rateNote = (r: FxRate, fx: TDocumentFx) =>
  [
    r.leg ? WHAT[r.leg]?.(r) : null,
    `${r.event ? WHY[r.event] : 'Rate'}, ${dayOf(r, fx)}.`,
  ]
    .filter(Boolean)
    .join(' ');

/**
 * The rate this document is billed at: what converts its agreed amounts into its own currency,
 * with the day of the rate in the tooltip - one line per currency. How the plan valued a mixed
 * order is a step before this document and is not shown here.
 */
function FxFact({ fx }: { fx: TDocumentFx | null | undefined }) {
  if (!fx || fx.rates.length === 0) return null;
  const shown = billedRates(fx);
  return (
    <FactRow label="Rate">
      {shown.map((r) => (
        <Tooltip
          key={`${r.from}${r.to}${r.rate}`}
          label={rateNote(r, fx)}
          multiline
          w={260}
          fz="xs"
        >
          <Text fz="xs" style={{ cursor: 'help' }} component="div">
            {rateText(r)}
          </Text>
        </Tooltip>
      ))}
    </FactRow>
  );
}

/**
 * Payment card of a proforma: method, rate, and the money moved on it with its statements. How
 * much was collected is stated once on the page: for an installment by the plan's totals in the
 * related documents, otherwise here.
 */
export function ProformaPaymentCard({ invoice }: { invoice: InvoiceDetails }) {
  const { permissions } = usePermissions();
  return (
    <DocumentCard title="Payment" icon={<PiMoney size={14} />}>
      <Stack gap={6}>
        {!invoice.installments && (
          <CollectedIndicator
            collected={invoice.paidAmount ?? 0}
            due={invoice.dueAmount ?? 0}
            currency={invoice.dueCurrency}
          />
        )}
        <FactRow label="Method">
          {invoice.paymentMethod} · {invoice.dueCurrency}
        </FactRow>
        <FxFact fx={invoice.fx} />
        <PaymentLedger
          invoice={invoice}
          canChange={invoiceActionGates(invoice, permissions).removeReport}
        />
      </Stack>
    </DocumentCard>
  );
}

/**
 * Payment card of a final invoice, laid out as its proforma's: the money facts are the proforma's -
 * its total, method and rate, and the money moved on it with the statements behind it.
 */
export function FinalPaymentCard({ f }: { f: TFinalInvoiceDetails }) {
  const { permissions } = usePermissions();
  const proforma = useInvoiceDetails(f.proforma.id).data;
  const short = Math.abs(f.proforma.collected - f.amountDue) > 0.005;
  return (
    <DocumentCard title="Payment" icon={<PiMoney size={14} />}>
      <Stack gap={6}>
        <FactRow label="Proforma">
          <Anchor component={Link} to={`~/invoices/${f.proforma.id}`} fz="xs">
            {f.proforma.number}
          </Anchor>
        </FactRow>
        <FactRow label="Total">{money(f.amountDue, f.dueCurrency)}</FactRow>
        {short && (
          <FactRow label="Collected">
            <Text fz="xs" c="orange">
              {money(f.proforma.collected, f.dueCurrency)}
            </Text>
          </FactRow>
        )}
        {proforma && (
          <FactRow label="Method">
            {proforma.paymentMethod} · {f.dueCurrency}
          </FactRow>
        )}
        <FxFact fx={f.fx} />
        {proforma && (
          <PaymentLedger
            invoice={proforma}
            canChange={invoiceActionGates(proforma, permissions).removeReport}
          />
        )}
      </Stack>
    </DocumentCard>
  );
}
